const DEFAULT_MAX_LENGTH = 10;

const STOP_WORDS = new Set([
  "a",
  "an",
  "the",
  "and",
  "or",
  "of",
  "for",
  "at",
  "by",
  "to",
  "in",
  "on",
  "with",
  "from",
  "site",
  "centre",
  "center",
  "unit",
  "department",
  "dept",
  "division",
  "branch",
  "office",
  "campus",
  "location",
  "ltd",
  "limited",
  "llc",
  "inc",
  "plc",
  "uk",
  "nhs",
]);

const VOWELS = new Set(["A", "E", "I", "O", "U"]);

const cleanToken = (token = "") =>
  String(token)
    .replace(/['']/g, "")
    .replace(/[^a-zA-Z0-9]/g, "")
    .trim();

const splitSiteNameTokens = (siteName = "") =>
  String(siteName)
    .split(/[\s\-–—/&,+]+/)
    .map(cleanToken)
    .filter(Boolean);

const isStopWord = (word = "") => STOP_WORDS.has(word.toLowerCase());

const pickSignificantWords = (tokens = []) => {
  const significant = tokens.filter((word) => !isStopWord(word));
  return significant.length ? significant : tokens;
};

const abbreviateSingleToken = (word, maxLen) => {
  const w = cleanToken(word);
  if (!w) return "";
  if (w.length <= 2) return w.toUpperCase();
  if (w.length === 3) return w.toUpperCase();

  const upper = w.toUpperCase();
  const consonantSkeleton = upper
    .split("")
    .filter((char, index) => index === 0 || !VOWELS.has(char))
    .join("");

  if (consonantSkeleton.length >= 3) {
    return consonantSkeleton.slice(0, maxLen);
  }

  return upper.slice(0, Math.min(3, maxLen));
};

const abbreviateFromWords = (words, maxLen) => {
  if (!words.length) return "";

  if (words.length === 1) {
    return abbreviateSingleToken(words[0], maxLen);
  }

  const initials = words
    .map((word) => abbreviateSingleToken(word, maxLen).charAt(0))
    .filter(Boolean)
    .join("");

  if (initials.length >= 2) {
    return initials.slice(0, maxLen);
  }

  return abbreviateSingleToken(words[0], maxLen);
};

/**
 * Builds a compact site abbreviation from a full site name.
 * Skips filler words, uses initials for multi-word names, and consonant skeleton for single words.
 */
export const suggestSiteAbbreviation = (
  siteName,
  maxLen = DEFAULT_MAX_LENGTH,
) => {
  const tokens = splitSiteNameTokens(siteName);
  if (!tokens.length) return "";

  const words = pickSignificantWords(tokens);
  const suggestion = abbreviateFromWords(words, maxLen);

  return suggestion.toUpperCase().slice(0, maxLen);
};

export const sanitizeSiteAbbreviation = (
  value,
  maxLen = DEFAULT_MAX_LENGTH,
) =>
  String(value ?? "")
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, "")
    .slice(0, maxLen);
